import {
  createMassMessage,
  useGetMassMessageAudience,
  useListMassMessages,
  useListProperties,
  type MassMessageOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { TablePager, useTablePage } from '../../components/TablePager';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/permissions';

const COLUMNS: DataColumn<MassMessageOut>[] = [
  { label: 'Subject', render: (m) => m.subject },
  { label: 'Sent To', render: (m) => m.audience },
  { label: 'Written', render: (m) => formatDateTime(m.created_at) },
  { label: 'Recipients', align: 'right', render: (m) => (m.sent_at ? m.recipient_count : '–') },
  {
    label: 'Status',
    render: (m) =>
      m.status === 'sent' ? (
        <Chip size="small" color="success" label="Sent" />
      ) : (
        <Chip size="small" color="info" label="Sending Soon" />
      ),
  },
];

export function MessagesPage() {
  const canManage = useCan('notices.manage');
  const queryClient = useQueryClient();
  const paging = useTablePage();
  const query = useListMassMessages(paging.params);
  const messages = okItems(query.data);
  const properties = okItems(useListProperties({ page_size: 200 }).data);
  const [propertyId, setPropertyId] = useState('');
  const audience = okData(
    useGetMassMessageAudience(propertyId ? { property_id: Number(propertyId) } : {}).data,
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);

  async function send(event: FormEvent) {
    event.preventDefault();
    const count = audience?.with_email ?? 0;
    if (!window.confirm(`Send this to ${count} ${count === 1 ? 'resident' : 'residents'}?`)) return;
    setBusy(true);
    setError(null);
    setQueued(false);
    try {
      await createMassMessage({
        subject,
        body,
        property_id: propertyId ? Number(propertyId) : null,
      });
      setSubject('');
      setBody('');
      setQueued(true);
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h5">Messages</Typography>
        <Typography color="text.secondary">
          Announcements by email to residents on current leases, like a water shutoff.
        </Typography>
      </div>
      {canManage && (
        <Paper variant="outlined" sx={{ p: 3 }} component="form" onSubmit={(e) => void send(e)}>
          <Stack spacing={2}>
            <Typography variant="h6">New Message</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {queued && <Alert severity="success">Saved. It goes out within 15 minutes.</Alert>}
            <TextField
              select
              label="Send To"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              helperText={
                audience
                  ? `${audience.with_email} with an email address${
                      audience.without_email ? `, ${audience.without_email} without one` : ''
                    }`
                  : ' '
              }
            >
              <MenuItem value="">All Properties</MenuItem>
              {properties.map((property) => (
                <MenuItem key={property.id} value={String(property.id)}>
                  {property.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 150 } }}
            />
            <TextField
              required
              multiline
              minRows={4}
              label="Message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 8000 } }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !audience?.with_email}
              sx={{ alignSelf: 'flex-start' }}
            >
              Send Message
            </Button>
          </Stack>
        </Paper>
      )}
      <Stack spacing={1}>
        <Typography variant="h6">Sent Messages</Typography>
        {messages.length === 0 ? (
          <Typography color="text.secondary">No messages yet.</Typography>
        ) : (
          <>
            <DataTable columns={COLUMNS} rows={messages} rowKey={(m) => m.id} />
            <TablePager paging={paging} total={okData(query.data)?.total ?? 0} />
          </>
        )}
      </Stack>
    </Stack>
  );
}
